import type { MutableRefObject } from 'react'
import { TFunction } from 'i18next'
import type { FieldValues, FormState } from 'react-hook-form'
import { CustomerDocumentStatusType, CustomerEntityType, ScrollToField } from 'types/customers'
import { getFormattedSingleDateValue } from 'utils/date'
import { getTranslation } from '../../../../__mocks__/i18nextMock'
import { INDIVIDUAL_CUSTOMER_ERROR_MAPPING } from './constants'
import { INDIVIDUAL_CUSTOMER_LABEL_MAPPING } from './form'
import { customerDocumentStatusLabel, customerStatusLabel, customerTypeLabel } from './mappings'
import {
  BusinessCustomerFormData,
  BusinessCustomerPayload,
  EmbeddedCustomerStatus,
  IndividualCustomerFormData,
  IndividualCustomerFormKey,
  IndividualCustomerPayload,
  type BusinessCustomerAddress,
  type PublicInformation,
  type RegulatorInformation,
} from './types'

export const getCustomerTypeLabel = (type: CustomerEntityType, t: TFunction): string => {
  const mappedLabel = customerTypeLabel?.[type]
  if (mappedLabel) return t(`customers.${mappedLabel}`)
}

export const getCustomerStatusLabel = (status: EmbeddedCustomerStatus, t: TFunction): string => {
  const mappedLabel = customerStatusLabel?.[status]
  if (mappedLabel) return t(`embeddedCustomers.status.${mappedLabel}`)
}

const getOperationalAddress = (
  customerData: BusinessCustomerFormData
): BusinessCustomerAddress | undefined => {
  if (
    customerData?.isOperationalAddressDifferent !== 'true' ||
    !customerData?.operationalAddressCountryCode
  )
    return undefined

  return {
    countryCode: customerData.operationalAddressCountryCode,
    ...(customerData.operationalAddressStateCode && {
      stateCode: customerData.operationalAddressStateCode,
    }),
    ...(customerData.operationalAddressLine1 && {
      addressLine1: customerData.operationalAddressLine1,
    }),
    ...(customerData.operationalAddressLine2 && {
      addressLine2: customerData.operationalAddressLine2,
    }),
    ...(customerData.operationalAddressCity && { city: customerData.operationalAddressCity }),
    ...(customerData.operationalAddressPostalCode && {
      postalCode: customerData.operationalAddressPostalCode,
    }),
  }
}

const getRegulatorInformation = (
  customerData: BusinessCustomerFormData
): RegulatorInformation | undefined => {
  if (customerData?.isRegulated !== 'true') return undefined
  if (!customerData?.regulatorName || !customerData?.regulatorJurisdiction) return undefined
  return {
    regulatorName: customerData.regulatorName,
    regulatorJurisdiction: customerData.regulatorJurisdiction,
  }
}

const getPublicInformation = (
  customerData: BusinessCustomerFormData
): PublicInformation | undefined => {
  if (customerData?.isPubliclyListed !== 'true') return undefined
  if (!customerData?.listedExchange || !customerData?.tickerSymbol) return undefined
  return { listedExchange: customerData.listedExchange, tickerSymbol: customerData.tickerSymbol }
}

export const getBusinessCustomerPayload = (
  customerData: BusinessCustomerFormData
): BusinessCustomerPayload => {
  const isRegulated = customerData?.isRegulated === 'true'
  const isPubliclyListed = customerData?.isPubliclyListed === 'true'
  const operationalAddress = getOperationalAddress(customerData)
  const regulatorInformation = getRegulatorInformation(customerData)
  const publicInformation = getPublicInformation(customerData)

  return {
    name: customerData?.businessName,
    description: customerData?.businessDescription,
    industryReference: customerData?.industry,
    monthlyExpectedVolumesReference: customerData?.monthlyExpectedVolumes,
    address: {
      countryCode: customerData?.countryOfIncorporation,
      ...(customerData?.stateCode && { stateCode: customerData.stateCode }),
      ...(customerData?.addressLine1 && { addressLine1: customerData.addressLine1 }),
      ...(customerData?.addressLine2 && { addressLine2: customerData.addressLine2 }),
      ...(customerData?.city && { city: customerData.city }),
      ...(customerData?.postalCode && { postalCode: customerData.postalCode }),
    },
    ...(operationalAddress && { operationalAddress }),
    ...(customerData?.taxNumber && { taxNumber: customerData.taxNumber }),
    ...(customerData?.registrationNumber && {
      registrationNumber: customerData.registrationNumber,
    }),
    ...(customerData?.businessOperationsStartDate && {
      businessOperationsStartDate: getFormattedSingleDateValue(
        customerData.businessOperationsStartDate
      ),
    }),
    ...(customerData?.isRegulated && { isRegulated }),
    ...(regulatorInformation && { regulatorInformation }),
    ...(customerData?.isPubliclyListed && { isPubliclyListed }),
    ...(publicInformation && { publicInformation }),
  }
}

export const getIndividualFieldLabel = (key: IndividualCustomerFormKey): string => {
  const customLabel = INDIVIDUAL_CUSTOMER_LABEL_MAPPING?.[key]
  return getTranslation(customLabel ?? `formFields.${key}`)
}

export const getIndividualCustomerPayload = (
  customerData: IndividualCustomerFormData
): IndividualCustomerPayload => {
  return {
    firstName: customerData?.firstName,
    lastName: customerData?.lastName,
    dateOfBirth: getFormattedSingleDateValue(customerData?.dateOfBirth),
    emailAddress: customerData?.emailAddress,
    description: customerData?.description,
    nationality: customerData?.nationality,
    taxIdentification: {
      number: customerData?.taxIdentificationNumber,
      taxResidenceCountryCode: customerData?.taxResidenceCountryCode,
    },
    address: {
      addressLine1: customerData?.addressLine1,
      addressLine2: customerData?.addressLine2,
      city: customerData?.city,
      postalCode: customerData?.postalCode,
      countryCode: customerData?.countryCode,
      ...(customerData?.stateCode && {
        stateCode: customerData?.stateCode,
      }),
    },
    cdd: {
      employmentStatus: customerData?.employmentStatus,
      sourceOfFunds: customerData?.sourceOfFunds,
      pepStatus: customerData?.pepStatus,
      expectedMonthlyVolume: {
        amount: Number(customerData?.expectedMonthlyVolumeAmount),
        currency: customerData?.expectedMonthlyVolumeCurrency,
      },
      intendedUseOfAccount: customerData?.intendedUseOfAccount,
      estimatedYearlyIncome: customerData?.estimatedYearlyIncome,
      employmentIndustrySector: customerData?.employmentIndustrySector,
    },
  }
}

export const getCustomerDocumentStatusLabel = (
  status: CustomerDocumentStatusType,
  t: TFunction
): string => {
  const mappedLabel = customerDocumentStatusLabel?.[status]
  if (mappedLabel) return t(`embeddedCustomers.documentStatus.${mappedLabel}`)
}

export const getCddIndividual = (
  value: string,
  descriptions: Record<string, string>
): string | undefined => Object.keys(descriptions).find(key => descriptions[key] === value)

export const getErrorFormKey = (requestErrorKey: string) => {
  return requestErrorKey.split('.')?.pop()
}

export const getIndividualCustomerErrorKey = (errorKey: string): IndividualCustomerFormKey => {
  const mappedKey = INDIVIDUAL_CUSTOMER_ERROR_MAPPING[errorKey]
  if (!mappedKey) {
    return errorKey as IndividualCustomerFormKey
  }
  return mappedKey
}

const scrollToField: ScrollToField = ({ position = 0, element } = {}) => {
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'center' })
    return
  }
  window.scrollTo({ top: position, behavior: 'smooth' })
}

export const scrollToTopError = <TFieldValues extends FieldValues>(
  form: { formState: FormState<TFieldValues> },
  fieldRefs: MutableRefObject<Record<string, HTMLElement | null>>
) => {
  const currentErrors = form?.formState?.errors ?? {}
  const errorKeys = Object.keys(currentErrors)
  if (!errorKeys.length) return

  const errorEntries = errorKeys
    .map(key => {
      const el = getElementForField(key, fieldRefs)
      return el ? { key, el, y: getPageTop(el) } : null
    })
    .filter(Boolean) as { key: string; el: HTMLElement; y: number }[]

  if (!errorEntries.length) return

  errorEntries.sort((a, b) => a.y - b.y)
  const topMost = errorEntries[0]
  scrollToField({ element: topMost.el })
  focusInputForField(topMost.key)
}

export const focusInputForField = (fieldName: string) => {
  const el = document.querySelector(`[name="${fieldName}"]`) || document.getElementById(fieldName)
  if (el instanceof HTMLElement) {
    el.focus({ preventScroll: true })
  }
}

export const getPageTop = (element: HTMLElement): number => {
  const rect = element.getBoundingClientRect()
  return rect.top + window.scrollY
}

export const getElementForField = (
  fieldName: string,
  fieldRefs: MutableRefObject<Record<string, HTMLElement | null>>
): HTMLElement | null => {
  const wrapper = fieldRefs.current?.[fieldName]
  if (wrapper) return wrapper
  const el = document.querySelector(`[name="${fieldName}"]`) || document.getElementById(fieldName)
  return el instanceof HTMLElement ? el : null
}

export type FormatValueInput =
  | string
  | number
  | boolean
  | null
  | undefined
  | Date
  | FormatValueInput[]
  | Record<string, unknown>

export const formatValue = (value: FormatValueInput): string => {
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No'
  }
  if (value == null) {
    return ''
  }
  if (Array.isArray(value)) {
    return value.map(item => formatValue(item)).join(', ')
  }
  if (value instanceof Date) {
    return value.toLocaleString()
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value)
    } catch {
      return ''
    }
  }
  return String(value)
}
